import { useState, useCallback } from 'react';
import type { BulkOperation } from '../types/collections';
import type { TarotReading } from '../types/tarot';

export function useBulkOperations() {
  const [selectedReadings, setSelectedReadings] = useState<Set<string>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  const toggleSelection = useCallback((readingId: string) => {
    setSelectedReadings(prev => {
      const newSet = new Set(prev);
      if (newSet.has(readingId)) {
        newSet.delete(readingId);
      } else {
        newSet.add(readingId);
      }
      return newSet;
    });
  }, []);

  const selectAll = useCallback((readings: TarotReading[]) => {
    setSelectedReadings(new Set(readings.map(r => r.id)));
  }, []);

  const selectNone = useCallback(() => {
    setSelectedReadings(new Set());
  }, []);

  const selectFiltered = useCallback((readings: TarotReading[]) => {
    setSelectedReadings(new Set(readings.map(r => r.id)));
  }, []);

  const enterSelectionMode = useCallback(() => {
    setIsSelectionMode(true);
  }, []);

  const exitSelectionMode = useCallback(() => {
    setIsSelectionMode(false);
    setSelectedReadings(new Set());
  }, []);

  const executeBulkOperation = useCallback(async (
    operation: BulkOperation,
    onFavorite?: (readingId: string, isFavorite: boolean) => Promise<void>,
    onDelete?: (readingId: string) => Promise<void>,
    onAddTag?: (readingId: string, tag: string) => Promise<void>,
    onRemoveTag?: (readingId: string, tag: string) => Promise<void>,
    onAddToCollection?: (readingId: string, collectionId: string) => Promise<void>,
    onRemoveFromCollection?: (readingId: string, collectionId: string) => Promise<void>
  ) => {
    const { type, readingIds, data } = operation;

    try {
      switch (type) {
        case 'favorite':
          if (onFavorite) {
            await Promise.all(readingIds.map(id => onFavorite(id, true)));
          }
          break;

        case 'unfavorite':
          if (onFavorite) {
            await Promise.all(readingIds.map(id => onFavorite(id, false)));
          }
          break;

        case 'delete':
          if (onDelete) {
            await Promise.all(readingIds.map(id => onDelete(id)));
          }
          break;

        case 'addTag':
          if (onAddTag && data?.tag) {
            await Promise.all(readingIds.map(id => onAddTag(id, data.tag!)));
          }
          break;

        case 'removeTag':
          if (onRemoveTag && data?.tag) {
            await Promise.all(readingIds.map(id => onRemoveTag(id, data.tag!)));
          }
          break;

        case 'addToCollection':
          if (onAddToCollection && data?.collectionId) {
            await Promise.all(readingIds.map(id => onAddToCollection(id, data.collectionId!)));
          }
          break;

        case 'removeFromCollection':
          if (onRemoveFromCollection && data?.collectionId) {
            await Promise.all(readingIds.map(id => onRemoveFromCollection(id, data.collectionId!)));
          }
          break;
      }

      // Clear selection after successful operation
      setSelectedReadings(new Set());
    } catch (error) {
      console.error('Bulk operation failed:', error);
      throw error;
    }
  }, []);

  const getSelectionStats = useCallback((readings: TarotReading[]) => {
    const selectedReadingObjects = readings.filter(r => selectedReadings.has(r.id));
    
    return {
      total: selectedReadings.size,
      favorites: selectedReadingObjects.filter(r => r.isFavorite).length,
      private: selectedReadingObjects.filter(r => r.isPrivate).length,
      completed: selectedReadingObjects.filter(r => r.status === 'completed').length,
      commonTags: getCommonTags(selectedReadingObjects)
    };
  }, [selectedReadings]);

  const getCommonTags = (readings: TarotReading[]) => {
    if (readings.length === 0) return [];
    
    const tagCounts = new Map<string, number>();
    readings.forEach(reading => {
      reading.tags.forEach(tag => {
        tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
      });
    });

    // Return tags that appear in all selected readings
    return Array.from(tagCounts.entries())
      .filter(([, count]) => count === readings.length)
      .map(([tag]) => tag);
  };

  return {
    selectedReadings: Array.from(selectedReadings),
    selectedReadingsSet: selectedReadings,
    isSelectionMode,
    toggleSelection,
    selectAll,
    selectNone,
    selectFiltered,
    enterSelectionMode,
    exitSelectionMode,
    executeBulkOperation,
    getSelectionStats
  };
}